/**
 * Supabase API helper that works with Netlify Identity authentication
 * This provides a simple interface for CRUD operations through serverless functions
 */
import store from "../store/store";
import tokenManager from "./token-manager";

/**
 * Get the base URL for Netlify functions
 * When on localhost, use the siteURL from store if set, otherwise use relative path
 * @returns {string} The base URL for function calls
 */
function getFunctionsBaseUrl() {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname.match(/^\d+\.\d+\.\d+\.\d+$/);
  
  if (isLocalhost) {
    const siteURL = store.getters['app/siteURL'];
    if (siteURL) {
      return `https://${siteURL}`;
    }
  }
  
  return ''; // Relative path for production or localhost without siteURL
}

/**
 * Make a request to the Supabase API through Netlify Functions
 * @param {string} table - The table name to operate on
 * @param {string} operation - The operation to perform (select, insert, update, delete)
 * @param {Object} data - The data for the operation
 * @param {Object} filters - Filters for select, update, delete operations
 * @returns {Promise<Object>} The response data
 */
export async function supabaseRequest(table, operation, data = null, filters = null) {
  try {
    // Ensure we have a valid token before making the request
    const tokenValid = await tokenManager.ensureValidToken();
    if (!tokenValid) {
      throw new Error('Unable to obtain valid authentication token');
    }

    // Get the current user's token for authentication
    const currentUser = store.getters['auth/currentUser'];
    if (!currentUser || !currentUser.token?.access_token) {
      throw new Error('User not authenticated');
    }

    const baseUrl = getFunctionsBaseUrl();
    const response = await fetch(`${baseUrl}/.netlify/functions/supabase-api`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentUser.token.access_token}`,
      },
      body: JSON.stringify({
        table,
        operation,
        data,
        filters
      })
    });

    // Handle 401 errors specifically - try to refresh token and retry once
    if (response.status === 401) {
      const refreshed = await tokenManager.handleApiError(response);
      
      if (refreshed) {
        // Retry the request with the new token
        const refreshedUser = store.getters['auth/currentUser'];
        if (refreshedUser && refreshedUser.token?.access_token) {
          const retryResponse = await fetch(`${baseUrl}/.netlify/functions/supabase-api`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${refreshedUser.token.access_token}`,
            },
            body: JSON.stringify({
              table,
              operation,
              data,
              filters
            })
          });

          if (retryResponse.ok) {
            const retryResponseText = await retryResponse.text();
            return retryResponseText ? JSON.parse(retryResponseText) : [];
          }
        }
      }
      
      throw new Error('Authentication failed - please log in again');
    }

    if (!response.ok) {
      let errorMessage = 'API request failed';
      try {
        const errorData = await response.text();
        const parsedError = JSON.parse(errorData);
        errorMessage = parsedError.error || errorMessage;
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError);
      }
      throw new Error(errorMessage);
    }

    const responseText = await response.text();
    
    if (!responseText) {
      return [];
    }

    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      console.error('Failed to parse response as JSON:', parseError, 'Response:', responseText);
      throw new Error('Invalid response format from server');
    }
  } catch (error) {
    console.error('Supabase request error:', error);
    throw error;
  }
}

/**
 * Select records from a table
 * @param {string} table - Table name
 * @param {string} select - Columns to select (default: '*')
 * @param {Object} filters - Where conditions
 * @returns {Promise<Array>} Array of records
 */
export async function selectFromSupabase(table, select = '*', filters = {}) {
  return await supabaseRequest(table, 'select', select, filters);
}

/**
 * Fetch current user profile from Supabase
 * @returns {Promise<Object>} User profile data including trial_credits
 */
export async function fetchUserProfile() {
  try {
    const currentUser = store.getters['auth/currentUser'];
    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }

    const result = await selectFromSupabase('users', 'trial_credits, subscription, stripe_id', {
      netlify_id: currentUser.id
    });

    return result && result.length > 0 ? result[0] : null;
  } catch (error) {
    console.error('Failed to fetch user profile:', error);
    return null;
  }
}

/**
 * Insert a record into a table
 * @param {string} table - Table name
 * @param {Object} data - Data to insert
 * @returns {Promise<Object>} Inserted record
 */
export async function insertIntoSupabase(table, data) {
  const result = await supabaseRequest(table, 'insert', data);
  return result[0]; // Return the first (and only) inserted record
}

/**
 * Update records in a table
 * @param {string} table - Table name
 * @param {Object} data - Data to update
 * @param {Object} filters - Where conditions
 * @returns {Promise<Array>} Updated records
 */
export async function updateInSupabase(table, data, filters) {
  return await supabaseRequest(table, 'update', data, filters);
}

/**
 * Delete records from a table
 * @param {string} table - Table name
 * @param {Object} filters - Where conditions
 * @returns {Promise<Array>} Deleted records
 */
export async function deleteFromSupabase(table, filters) {
  return await supabaseRequest(table, 'delete', null, filters);
}

/**
 * Make a request to the Reports API through Netlify Functions
 * @param {string} operation - The operation to perform (select, selectRange, insert, update, delete)
 * @param {Object} data - The data for the operation
 * @param {Object} filters - Filters for select, update, delete operations
 * @param {string} from - Start date for range queries (ISO string)
 * @param {string} to - End date for range queries (ISO string)
 * @returns {Promise<Object>} The response data
 */
export async function reportsRequest(operation, data = null, filters = null, from = null, to = null) {
  try {
    // Ensure we have a valid token before making the request
    const tokenValid = await tokenManager.ensureValidToken();
    if (!tokenValid) {
      throw new Error('Unable to obtain valid authentication token');
    }

    // Get the current user's token for authentication
    const currentUser = store.getters['auth/currentUser'];
    if (!currentUser || !currentUser.token?.access_token) {
      throw new Error('User not authenticated');
    }

    const baseUrl = getFunctionsBaseUrl();
    const response = await fetch(`${baseUrl}/.netlify/functions/reports-api`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentUser.token.access_token}`,
      },
      body: JSON.stringify({
        operation,
        data,
        filters,
        from,
        to
      })
    });


    // Handle 401 errors specifically - try to refresh token and retry once
    if (response.status === 401) {
      console.log('Received 401 from reports API, attempting token refresh and retry...');
      const refreshed = await tokenManager.handleApiError(response);
      
      if (refreshed) {
        // Retry the request with the new token
        const refreshedUser = store.getters['auth/currentUser'];
        if (refreshedUser && refreshedUser.token?.access_token) {
          const retryResponse = await fetch(`${baseUrl}/.netlify/functions/reports-api`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${refreshedUser.token.access_token}`,
            },
            body: JSON.stringify({
              operation,
              data,
              filters,
              from,
              to
            })
          });

          if (retryResponse.ok) {
            const retryResponseText = await retryResponse.text();
            return retryResponseText ? JSON.parse(retryResponseText) : [];
          }
        }
      }
      
      throw new Error('Authentication failed - please log in again');
    }

    if (!response.ok) {
      let errorMessage = 'Reports API request failed';
      try {
        const errorData = await response.text();
        console.log('Reports error response text:', errorData);
        const parsedError = JSON.parse(errorData);
        errorMessage = parsedError.error || errorMessage;
      } catch (parseError) {
        console.error('Failed to parse reports error response:', parseError);
      }
      throw new Error(errorMessage);
    }

    const responseText = await response.text();
    
    if (!responseText) {
      return [];
    }

    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      console.error('Failed to parse reports response as JSON:', parseError, 'Response:', responseText);
      throw new Error('Invalid response format from reports server');
    }
  } catch (error) {
    console.error('Reports request error:', error);
    throw error;
  }
}

/**
 * Select reports with date range filtering
 * @param {string} from - Start date (ISO string)
 * @param {string} to - End date (ISO string)
 * @param {string} select - Columns to select (default: '*')
 * @returns {Promise<Array>} Array of reports within the date range
 */
export async function selectReportsRange(from, to, select = '*') {
  return await reportsRequest('selectRange', select, null, from, to);
}

/**
 * Make a request to the Tracking API through Netlify Functions
 * @param {string} operation - The operation to perform (selectRange)
 * @param {Object|string|null} data - Select columns (default: '*')
 * @param {string|null} from - Start timestamp (ms)
 * @param {string|null} to - End timestamp (ms)
 * @returns {Promise<Object>} The response data
 */
export async function trackingRequest(operation, data = null, from = null, to = null) {
  try {
    const tokenValid = await tokenManager.ensureValidToken();
    if (!tokenValid) {
      throw new Error('Unable to obtain valid authentication token');
    }

    const currentUser = store.getters['auth/currentUser'];
    if (!currentUser || !currentUser.token?.access_token) {
      throw new Error('User not authenticated');
    }

    const baseUrl = getFunctionsBaseUrl();
    const response = await fetch(`${baseUrl}/.netlify/functions/tracking-api`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentUser.token.access_token}`,
      },
      body: JSON.stringify({
        operation,
        data,
        from,
        to,
      })
    });

    if (response.status === 401) {
      const refreshed = await tokenManager.handleApiError(response);
      if (refreshed) {
        const refreshedUser = store.getters['auth/currentUser'];
        if (refreshedUser && refreshedUser.token?.access_token) {
          const retryResponse = await fetch(`${baseUrl}/.netlify/functions/tracking-api`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${refreshedUser.token.access_token}`,
            },
            body: JSON.stringify({
              operation,
              data,
              from,
              to,
            })
          });

          if (retryResponse.ok) {
            const retryResponseText = await retryResponse.text();
            return retryResponseText ? JSON.parse(retryResponseText) : [];
          }
        }
      }

      throw new Error('Authentication failed - please log in again');
    }

    if (!response.ok) {
      let errorMessage = 'Tracking API request failed';
      try {
        const errorData = await response.text();
        const parsedError = JSON.parse(errorData);
        errorMessage = parsedError.error || errorMessage;
      } catch (parseError) {
        console.error('Failed to parse tracking error response:', parseError);
      }
      throw new Error(errorMessage);
    }

    const responseText = await response.text();
    if (!responseText) {
      return [];
    }

    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      console.error('Failed to parse tracking response as JSON:', parseError, 'Response:', responseText);
      throw new Error('Invalid response format from tracking server');
    }
  } catch (error) {
    console.error('Tracking request error:', error);
    throw error;
  }
}

/**
 * Select tracking elements with date range filtering
 * @param {string|number} from - Start timestamp (ms)
 * @param {string|number} to - End timestamp (ms)
 * @param {string} select - Columns to select (default: '*')
 * @returns {Promise<Array>} Array of tracking elements within the date range
 */
export async function selectTrackingRange(from, to, select = '*') {
  return await trackingRequest('selectRange', select, from, to);
}